import { ReactComponent as FacebookIcon } from 'assets/icons/facebook.svg';
import { ReactComponent as InstagramIcon } from 'assets/icons/instagram.svg';
import { ReactComponent as LinkedinIcon } from 'assets/icons/linkedin.svg';
import { ReactComponent as PinterestIcon } from 'assets/icons/pinterest.svg';
import { ReactComponent as TwitterIcon } from 'assets/icons/twitter.svg';
import { DropdownPopover } from 'modules/common/components/ToolbarPopover';
import { generateTranslatedOptions } from 'modules/common/components/ToolbarPopover/DropdownPopover';
import { ReactElement, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperType } from 'types/paper.types';

interface PaperTypeSelectProps {
  value?: PaperType;
  onChange?: (newType: PaperType, idx: number) => void;
}

const DEFAULT_ICON_WIDTH = 15;

const DROPDOWN_OPTIONS = [
  {
    value: '',
    label: 'pageSize.paperType.custom',
  },
  {
    value: '',
    label: '',
    isDivider: true,
  },
  {
    value: '',
    label: 'pageSize.paperType.documents',
    isSubHeader: true,
  },
  {
    value: PaperType.LETTER,
    label: 'pageSize.paperType.letter',
  },
  {
    value: PaperType.LEGAL,
    label: 'pageSize.paperType.legal',
  },
  {
    value: PaperType.TABLOID,
    label: 'pageSize.paperType.tabloid',
  },
  {
    value: PaperType.A3,
    label: 'pageSize.paperType.a3',
  },
  {
    value: PaperType.A4,
    label: 'pageSize.paperType.a4',
  },
  {
    value: PaperType.A5,
    label: 'pageSize.paperType.a5',
  },
  {
    value: PaperType.PRESENTATION,
    label: 'pageSize.paperType.presentation',
  },
  {
    value: '',
    label: '',
    isDivider: true,
  },
  {
    value: '',
    label: 'pageSize.paperType.socialMedia',
    isSubHeader: true,
  },
  {
    value: PaperType.INSTAGRAM,
    label: 'pageSize.paperType.instagram',
    icon: <InstagramIcon width={DEFAULT_ICON_WIDTH} />,
  },
  {
    value: PaperType.FACEBOOK,
    label: 'pageSize.paperType.facebook',
    icon: <FacebookIcon width={DEFAULT_ICON_WIDTH} />,
  },
  {
    value: PaperType.LINKEDIN,
    label: 'pageSize.paperType.linkedIn',
    icon: <LinkedinIcon width={DEFAULT_ICON_WIDTH} />,
  },
  {
    value: PaperType.TWITTER,
    label: 'pageSize.paperType.twitter',
    icon: <TwitterIcon width={DEFAULT_ICON_WIDTH} />,
  },
  {
    value: PaperType.PINTEREST,
    label: 'pageSize.paperType.pinterest',
    icon: <PinterestIcon width={DEFAULT_ICON_WIDTH} />,
  },
];

const PaperTypeSelect = memo(({ value, onChange }: PaperTypeSelectProps): ReactElement => {
  const { t } = useTranslation('editor_toolbar_page_menu', { useSuspense: false });

  const selectedOptionIndex = !value
    ? 0
    : Object.values(DROPDOWN_OPTIONS).findIndex((option) => {
        return option.value === value;
      });

  return (
    <>
      <DropdownPopover
        options={generateTranslatedOptions(t, DROPDOWN_OPTIONS)}
        selectedIndex={selectedOptionIndex ?? 0}
        label={t('pageSize.paperType.tooltip')}
        w={'14rem'}
        h={'24rem'}
        spacing={1}
        buttonVariant={'toolbar-dropdown-option'}
        optionsButtonVariant={'toolbar-dropdown-item'}
        onSelect={onChange}
        triggerSelectOnSameValue={false}
        closeOnSelect={true}
        triggerProps={{
          height: 8,
          'data-testid': 'paper-type-trigger',
        }}
        buttonOptionProps={{
          height: 7,
          justifyContent: 'flex-start',
        }}
        useLabelInsteadOfSelectedLabel
      />
    </>
  );
});
PaperTypeSelect.displayName = 'PaperTypeSelect';

export { PaperTypeSelect };
